import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import {
  Globe, Plug, EnvelopeSimple, FileText, MagnifyingGlass, Database,
  CircleNotch, CaretRight, Shield, Lightning, CheckCircle, Cpu,
  ArrowLeft, Lock, Gear, Eye
} from '@phosphor-icons/react';

const ICON_MAP = {
  globe: Globe,
  plug: Plug,
  mail: EnvelopeSimple,
  'file-text': FileText,
  search: MagnifyingGlass,
  database: Database,
};

const TYPE_LABELS = {
  browser: 'Browser',
  api_connector: 'API Connector',
  email: 'Email',
  file_processing: 'File Processing',
  search: 'Search',
  memory: 'Memory / Storage',
  custom: 'Custom',
};

const AppCard = ({ app, onClick }) => {
  const Icon = ICON_MAP[app.icon] || Cpu;
  return (
    <div
      data-testid={`app-card-${app.app_id}`}
      onClick={onClick}
      className="group bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-5 cursor-pointer hover:border-[var(--brand-primary)]/40 hover:shadow-lg hover:shadow-[var(--brand-primary)]/5 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[var(--brand-primary)]/20 transition-colors">
          <Icon size={22} weight="duotone" className="text-[var(--brand-primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{app.name}</h3>
            <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[9px] font-medium uppercase">Native</span>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{app.description}</p>
          <div className="flex items-center gap-2 mt-2.5">
            <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><Lightning size={10} weight="bold" />{app.capabilities?.length || 0} capabilities</span>
            <span className="text-[var(--text-muted)]">&middot;</span>
            <span className={`text-[10px] ${app.status === 'active' ? 'text-emerald-400' : 'text-gray-400'}`}>
              {app.status === 'active' ? 'Active' : app.status}
            </span>
          </div>
        </div>
        <CaretRight size={16} className="text-[var(--text-muted)] group-hover:text-[var(--brand-primary)] transition-colors flex-shrink-0 mt-1" />
      </div>
    </div>
  );
};

const AppDetailPanel = ({ app, onClose }) => {
  const Icon = ICON_MAP[app.icon] || Cpu;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid={`app-detail-${app.app_id}`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-6">
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"><ArrowLeft size={18} /></button>
            <span className="text-xs text-[var(--text-muted)]">App Library</span>
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[var(--brand-primary)]/10 flex items-center justify-center flex-shrink-0">
              <Icon size={28} weight="duotone" className="text-[var(--brand-primary)]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--text-primary)]">{app.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-full text-[10px] font-medium">Native App</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${app.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'}`}>
                  {app.status === 'active' ? 'Active' : app.status}
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-[var(--text-secondary)] mb-6 leading-relaxed">{app.description}</p>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-3 bg-[var(--surface-2)] rounded-xl">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Type</p>
              <p className="text-sm text-[var(--text-primary)]">{TYPE_LABELS[app.app_type] || app.app_type}</p>
            </div>
            <div className="p-3 bg-[var(--surface-2)] rounded-xl">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Trust Class</p>
              <p className="text-sm text-[var(--text-primary)] flex items-center gap-1"><Shield size={12} className="text-emerald-400" />{app.trust_class?.replace(/_/g, ' ')}</p>
            </div>
            <div className="p-3 bg-[var(--surface-2)] rounded-xl">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Auth Model</p>
              <p className="text-sm text-[var(--text-primary)] flex items-center gap-1"><Lock size={12} />{(app.auth_model || app.metadata?.auth_model || 'platform_managed')?.replace(/_/g, ' ')}</p>
            </div>
            <div className="p-3 bg-[var(--surface-2)] rounded-xl">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Developer</p>
              <p className="text-sm text-[var(--text-primary)]">{app.developer_name || 'Man & Machine'}</p>
            </div>
          </div>

          {/* Capabilities */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-1.5"><Lightning size={14} weight="bold" />Capabilities</p>
            <div className="flex flex-wrap gap-2">
              {(app.capabilities || []).map(cap => (
                <span key={cap} className="px-2.5 py-1 bg-[var(--brand-primary)]/8 text-[var(--brand-primary)] rounded-lg text-xs font-medium border border-[var(--brand-primary)]/15">
                  {cap.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>

          {/* Permissions */}
          {(app.permissions?.length > 0 || app.config_schema?.properties) && (
            <div className="mb-6">
              <p className="text-xs font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-1.5"><Gear size={14} />Configuration</p>
              {app.config_schema?.properties && (
                <div className="space-y-1.5">
                  {Object.entries(app.config_schema.properties).map(([key, prop]) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-[var(--surface-2)] rounded-lg">
                      <span className="text-xs text-[var(--text-primary)]">{key.replace(/_/g, ' ')}</span>
                      <span className="text-[10px] text-[var(--text-muted)]">{prop.type}{prop.default !== undefined ? ` = ${String(prop.default)}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tags */}
          {app.tags?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-[var(--text-primary)] mb-2">Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {app.tags.map(t => <span key={t} className="px-2 py-0.5 bg-[var(--surface-3)] rounded text-[10px] text-[var(--text-muted)]">{t}</span>)}
              </div>
            </div>
          )}

          {/* Provider Linkage */}
          <div className="mt-6 pt-4 border-t border-[var(--border-default)]">
            <div className="flex items-center justify-between text-[10px] text-[var(--text-muted)]">
              <span>Provider: {app.provider_id}</span>
              <span>Source: {app.source}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AppLibrary = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/apps`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setApps(data.apps || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  return (
    <div className="min-h-screen bg-[var(--surface-0)] p-6" data-testid="app-library-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">App Library</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Platform apps that power your AI agents and automations.</p>
        </div>

        {/* Apps Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : apps.length === 0 ? (
          <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)]">
            <Cpu size={40} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-[var(--text-secondary)] text-sm">No apps available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="app-grid">
            {apps.map(app => (
              <AppCard key={app.app_id} app={app} onClick={() => setSelectedApp(app)} />
            ))}
          </div>
        )}

        {/* Detail Panel */}
        {selectedApp && <AppDetailPanel app={selectedApp} onClose={() => setSelectedApp(null)} />}
      </div>
    </div>
  );
};

export default AppLibrary;
